/**
 * DieticianCard Component
 *
 * Card displaying dietician information with specialties.
 * Follows the established BiteWise component patterns and integrates
 * with existing UI components (Card, TitleText, SubtitleText, BodyText).
 */

import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Card } from '../ui/Card';
import { TitleText } from '../ui/TitleText';
import { SubtitleText } from '../ui/SubtitleText';
import { BodyText } from '../ui/BodyText';
import { AvatarPhoto } from '../ui/AvatarPhoto';
import type { DieticianCardProps } from '../../types/referralLanding';

/**
 * DieticianCard component for displaying dietician information
 *
 * @param props - DieticianCard component props
 * @returns JSX.Element
 */
export const DieticianCard: React.FC<DieticianCardProps> = ({
  dietician,
  isLoading = false,
  className = '',
}) => {
  const { isDark } = useTheme();

  // Loading skeleton
  if (isLoading) {
    return <DieticianCardSkeleton className={className} />;
  }

  const cardBgColor = isDark ? 'bg-secondary-800' : 'bg-white';
  const borderColor = isDark ? 'border-secondary-700' : 'border-secondary-200';

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-6">
        {/* Header with avatar and basic info */}
        <div className="flex items-start space-x-4 mb-4">
          <AvatarPhoto
            src={dietician.profilePhotoUrl}
            firstName={dietician.firstName}
            lastName={dietician.lastName}
            size="lg"
          />

          <div className="flex-1 min-w-0">
            <TitleText
              level={3}
              className="text-lg font-semibold mb-1 truncate"
            >
              {dietician.title} {dietician.firstName} {dietician.lastName}
            </TitleText>

            {/* Credentials */}
            {dietician.credentials.length > 0 && (
              <SubtitleText className="mb-2">
                {dietician.credentials.join(', ')}
              </SubtitleText>
            )}

            <BodyText variant="secondary" size="sm">
              {dietician.organization}
            </BodyText>
          </div>
        </div>

        {/* Experience */}
        {dietician.yearsOfExperience && (
          <div className="mb-4">
            <BodyText size="sm" className="font-medium">
              {dietician.yearsOfExperience} years of experience
            </BodyText>
          </div>
        )}

        {/* Specialties */}
        {dietician.specialties.length > 0 && (
          <div className="mb-4">
            <BodyText size="sm" className="font-medium mb-2">
              Specialties:
            </BodyText>
            <div className="flex flex-wrap gap-2">
              {dietician.specialties.map((specialty, index) => (
                <span
                  key={index}
                  className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${isDark
                      ? 'bg-secondary-700 text-secondary-100 border border-secondary-600'
                      : 'bg-secondary-100 text-secondary-700 border border-secondary-200'
                    }
                  `.trim()}
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bio */}
        {dietician.bio && (
          <div className="mb-4">
            <BodyText size="sm" className="leading-relaxed">
              {dietician.bio}
            </BodyText>
          </div>
        )}

        {/* Contact information */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <EmailIcon className="w-4 h-4 text-secondary-500" />
            <BodyText size="sm" className="truncate">
              {dietician.email}
            </BodyText>
          </div>

          {dietician.phone && (
            <div className="flex items-center space-x-2">
              <PhoneIcon className="w-4 h-4 text-secondary-500" />
              <BodyText size="sm">
                {dietician.phone}
              </BodyText>
            </div>
          )}
        </div>

        {/* Assigned dietician label */}
        <div className={`pt-4 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
          <div className="flex items-center space-x-2">
            <div className={`
              w-2 h-2 rounded-full
              ${isDark ? 'bg-secondary-400' : 'bg-secondary-500'}
            `} />
            <BodyText variant="secondary" size="sm" className="font-medium">
              Assigned Dietician
            </BodyText>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * DieticianCard loading skeleton
 *
 * @param props - Skeleton props
 * @returns JSX.Element
 */
export const DieticianCardSkeleton: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  const { isDark } = useTheme();

  const cardBgColor = isDark ? 'bg-secondary-800' : 'bg-white';
  const borderColor = isDark ? 'border-secondary-700' : 'border-secondary-200';
  const skeletonColor = isDark ? 'bg-secondary-700' : 'bg-secondary-200';

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-6 animate-pulse">
        {/* Header skeleton */}
        <div className="flex items-start space-x-4 mb-4">
          <AvatarPhoto
            size="lg"
            loading={true}
          />

          <div className="flex-1 space-y-2">
            <div className={`h-5 ${skeletonColor} rounded w-3/4`} />
            <div className={`h-4 ${skeletonColor} rounded w-1/2`} />
            <div className={`h-3 ${skeletonColor} rounded w-2/3`} />
          </div>
        </div>

        {/* Experience skeleton */}
        <div className="mb-4">
          <div className={`h-3 ${skeletonColor} rounded w-32`} />
        </div>

        {/* Specialties skeleton */}
        <div className="mb-4">
          <div className={`h-3 ${skeletonColor} rounded w-20 mb-2`} />
          <div className="flex flex-wrap gap-2">
            <div className={`h-6 ${skeletonColor} rounded-full w-16`} />
            <div className={`h-6 ${skeletonColor} rounded-full w-20`} />
            <div className={`h-6 ${skeletonColor} rounded-full w-24`} />
          </div>
        </div>

        {/* Bio skeleton */}
        <div className="mb-4 space-y-2">
          <div className={`h-3 ${skeletonColor} rounded w-full`} />
          <div className={`h-3 ${skeletonColor} rounded w-5/6`} />
          <div className={`h-3 ${skeletonColor} rounded w-4/5`} />
        </div>

        {/* Contact skeleton */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 ${skeletonColor} rounded`} />
            <div className={`h-3 ${skeletonColor} rounded w-48`} />
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 ${skeletonColor} rounded`} />
            <div className={`h-3 ${skeletonColor} rounded w-32`} />
          </div>
        </div>

        {/* Footer skeleton */}
        <div className={`pt-4 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 ${skeletonColor} rounded-full`} />
            <div className={`h-3 ${skeletonColor} rounded w-28`} />
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Email icon component
 */
const EmailIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

/**
 * Phone icon component
 */
const PhoneIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

export default DieticianCard;